import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, Plus, Trash2 } from 'lucide-react';

import { CAPABILITY_OPTIONS } from '@/api/capabilities.api';
import type { Department } from '@/api/employees.api';
import { createSeat, updateSeat, SEAT_ROLES, type SeatScenario, type SeatView } from '@/api/seats.api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { roleLabel } from '@/lib/accountability/seatMeta';
import type { SystemRole } from '@/types';

/**
 * Create / edit a seat: name, purpose, roles & responsibilities list,
 * SystemRole (auditor excluded), capability bundle, department, and parent
 * seat. Client-side validation mirrors the server's Zod rules
 * (responsibilities ≤ 7, ≤ 200 chars each; name required). The server is
 * authoritative for cycle checks + guards.
 */

const MAX_RESPONSIBILITIES = 7;
const MAX_RESPONSIBILITY_LEN = 200;
const MAX_NAME_LEN = 120;

const SELECT_CLS =
  'w-full rounded-kw-md border border-kw-border bg-white px-2.5 py-2 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20';

export interface SeatEditorModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  /** The seat being edited (mode === 'edit'). */
  seat?: SeatView | null;
  /** Scenario the new seat belongs to (mode === 'create'). */
  scenario: SeatScenario;
  departments: Department[];
  /** All seats in the active scenario — the parent picker. */
  allSeats: SeatView[];
  onClose: () => void;
  onSaved: (seat: SeatView) => void;
}

export function SeatEditorModal({
  open,
  mode,
  seat,
  scenario,
  departments,
  allSeats,
  onClose,
  onSaved,
}: SeatEditorModalProps) {
  const [name, setName] = useState('');
  const [purpose, setPurpose] = useState('');
  const [responsibilities, setResponsibilities] = useState<string[]>([]);
  const [systemRole, setSystemRole] = useState<SystemRole>('employee');
  const [capabilities, setCapabilities] = useState<Set<string>>(new Set());
  const [departmentId, setDepartmentId] = useState('');
  const [parentSeatId, setParentSeatId] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Seed the form whenever the modal opens (or the target seat changes).
  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && seat) {
      setName(seat.name);
      setPurpose(seat.purpose ?? '');
      setResponsibilities(seat.responsibilities.length > 0 ? [...seat.responsibilities] : []);
      setSystemRole(seat.systemRole);
      setCapabilities(new Set(seat.capabilities));
      setDepartmentId(seat.departmentId ?? '');
      setParentSeatId(seat.parentSeatId ?? '');
    } else {
      setName('');
      setPurpose('');
      setResponsibilities([]);
      setSystemRole('employee');
      setCapabilities(new Set());
      setDepartmentId('');
      setParentSeatId('');
    }
    setError(null);
  }, [open, mode, seat]);

  // Parent options: same scenario, exclude self + descendants (obvious
  // cycles). The server still runs the authoritative cycle guard.
  const parentOptions = useMemo(() => {
    const forbidden = new Set<string>();
    if (mode === 'edit' && seat) {
      forbidden.add(seat.id);
      collectDescendants(seat.id, allSeats, forbidden);
    }
    return allSeats.filter((s) => s.scenario === scenario && !s.archivedAt && !forbidden.has(s.id));
  }, [allSeats, scenario, mode, seat]);

  const mutation = useMutation({
    mutationFn: async (): Promise<SeatView> => {
      const cleanResponsibilities = responsibilities.map((r) => r.trim()).filter((r) => r.length > 0);
      const payload = {
        name: name.trim(),
        purpose: purpose.trim() ? purpose.trim() : null,
        responsibilities: cleanResponsibilities,
        systemRole,
        capabilities: [...capabilities],
        departmentId: departmentId || null,
        parentSeatId: parentSeatId || null,
      };
      if (mode === 'edit' && seat) {
        return updateSeat(seat.id, payload);
      }
      return createSeat({ ...payload, scenario });
    },
    onSuccess: (saved) => {
      onSaved(saved);
      onClose();
    },
    onError: (err) => {
      setError(mutationMessage(err));
    },
  });

  function validate(): string | null {
    const trimmed = name.trim();
    if (!trimmed) return 'Give the seat a name.';
    if (trimmed.length > MAX_NAME_LEN) return `Name must be ${String(MAX_NAME_LEN)} characters or fewer.`;
    const filled = responsibilities.map((r) => r.trim()).filter((r) => r.length > 0);
    if (filled.length > MAX_RESPONSIBILITIES) {
      return `A seat can list at most ${String(MAX_RESPONSIBILITIES)} responsibilities.`;
    }
    if (filled.some((r) => r.length > MAX_RESPONSIBILITY_LEN)) {
      return `Each responsibility must be ${String(MAX_RESPONSIBILITY_LEN)} characters or fewer.`;
    }
    return null;
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    mutation.mutate();
  }

  function updateResponsibility(index: number, value: string): void {
    setResponsibilities((prev) => prev.map((r, i) => (i === index ? value : r)));
  }

  function toggleCapability(value: string): void {
    setCapabilities((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === 'edit' ? 'Edit seat' : 'Add seat'}
      size="lg"
      className="max-h-[90vh] overflow-y-auto"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Seat name"
          name="seat-name"
          required
          maxLength={MAX_NAME_LEN}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
          placeholder="e.g. Head of Operations"
        />

        <label className="block space-y-1.5">
          <span className="block text-[13px] font-medium text-kw-label">Purpose</span>
          <textarea
            name="seat-purpose"
            rows={2}
            maxLength={500}
            value={purpose}
            onChange={(e) => {
              setPurpose(e.target.value);
            }}
            placeholder="The one-line reason this seat exists."
            className="block w-full rounded-kw-md border border-kw-border bg-white px-3 py-2 text-[14px] text-kw-shark placeholder:text-kw-muted focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
          />
        </label>

        {/* Responsibilities */}
        <div className="space-y-1.5">
          <span className="block text-[13px] font-medium text-kw-label">
            Roles &amp; responsibilities{' '}
            <span className="font-normal text-kw-muted">(up to {MAX_RESPONSIBILITIES})</span>
          </span>
          <div className="space-y-2">
            {responsibilities.map((r, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  aria-label={`Responsibility ${String(i + 1)}`}
                  value={r}
                  maxLength={MAX_RESPONSIBILITY_LEN}
                  onChange={(e) => {
                    updateResponsibility(i, e.target.value);
                  }}
                  placeholder="e.g. Own the weekly production schedule"
                  className="min-w-0 flex-1 rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
                />
                <button
                  type="button"
                  aria-label={`Remove responsibility ${String(i + 1)}`}
                  onClick={() => {
                    setResponsibilities((prev) => prev.filter((_, idx) => idx !== i));
                  }}
                  className="rounded-kw-md p-1.5 text-kw-muted hover:bg-kw-bg hover:text-kw-danger-text"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
            ))}
          </div>
          {responsibilities.length < MAX_RESPONSIBILITIES ? (
            <button
              type="button"
              onClick={() => {
                setResponsibilities((prev) => [...prev, '']);
              }}
              className="inline-flex items-center gap-1 text-[12px] font-medium text-kw-cerulean hover:text-kw-cerulean-dark"
            >
              <Plus className="size-3.5" />
              Add responsibility
            </button>
          ) : null}
        </div>

        {/* Role + department */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block space-y-1.5">
            <span className="block text-[13px] font-medium text-kw-label">Seat role (grants access)</span>
            <select
              aria-label="Seat role"
              value={systemRole}
              onChange={(e) => {
                setSystemRole(e.target.value as SystemRole);
              }}
              className={SELECT_CLS}
            >
              {SEAT_ROLES.map((role) => (
                <option key={role} value={role}>
                  {roleLabel(role)}
                </option>
              ))}
            </select>
          </label>

          <label className="block space-y-1.5">
            <span className="block text-[13px] font-medium text-kw-label">Department</span>
            <select
              aria-label="Department"
              value={departmentId}
              onChange={(e) => {
                setDepartmentId(e.target.value);
              }}
              className={SELECT_CLS}
            >
              <option value="">No department</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* Parent seat */}
        <label className="block space-y-1.5">
          <span className="block text-[13px] font-medium text-kw-label">Reports into (parent seat)</span>
          <select
            aria-label="Parent seat"
            value={parentSeatId}
            onChange={(e) => {
              setParentSeatId(e.target.value);
            }}
            className={SELECT_CLS}
          >
            <option value="">Top of the chart (no parent)</option>
            {parentOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>

        {/* Capability bundle */}
        <fieldset className="space-y-2">
          <legend className="text-[13px] font-medium text-kw-label">
            Capability bundle{' '}
            <span className="font-normal text-kw-muted">(granted to whoever sits here)</span>
          </legend>
          <div className="grid max-h-52 grid-cols-1 gap-1.5 overflow-y-auto rounded-kw-md border border-kw-border p-2 sm:grid-cols-2">
            {CAPABILITY_OPTIONS.map((cap) => (
              <label
                key={cap.value}
                className="flex cursor-pointer items-start gap-2 rounded-kw-md p-1.5 hover:bg-kw-bg"
              >
                <input
                  type="checkbox"
                  checked={capabilities.has(cap.value)}
                  onChange={() => {
                    toggleCapability(cap.value);
                  }}
                  className="mt-0.5 size-4 shrink-0 accent-kw-cerulean"
                />
                <span className="min-w-0">
                  <span className="block text-[12px] font-medium text-kw-shark">{cap.label}</span>
                  <span className="block text-[11px] text-kw-muted">{cap.description}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        {error ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
            <p className="text-[12px] text-kw-danger-text">{error}</p>
          </div>
        ) : null}

        <div className="flex justify-end gap-2 border-t border-kw-border pt-4">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={!name.trim()}>
            {mode === 'edit' ? 'Save changes' : 'Create seat'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function collectDescendants(seatId: string, seats: SeatView[], into: Set<string>): void {
  for (const s of seats) {
    if (s.parentSeatId === seatId && !into.has(s.id)) {
      into.add(s.id);
      collectDescendants(s.id, seats, into);
    }
  }
}

function mutationMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { error?: unknown } } }).response;
    const code = response?.data?.error;
    if (code === 'would_create_seat_cycle') return 'That parent would create a reporting loop.';
    if (typeof code === 'string') return `Could not save the seat (${code}).`;
  }
  return 'Could not save the seat — please try again.';
}
